import { useLayoutEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { cn } from '@/lib/utils';
import { revealTab } from '@/lib/revealTab';
import { NAV_BADGE_LABELS, useNavBadges } from '@/app/hooks/useNavBadges';
import { activeNavItem, workspaceActions, workspaceForPath, workspaceTabs } from './navWorkspaces';
import { useNavPins } from './useNavPins';

/**
 * The area's tab strip for the current page, or null where there is none to
 * show: an area with one page and no actions (Today, an area a person's
 * grants cut to one tab), or the Tools footer. The desktop shell asks it too
 * (compact chrome, 2026-10-10): with a strip the area's tabs are its top row,
 * without one that row is a dock for the page's own title. Location and auth
 * only, so asking costs no request.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the shell reads the same answer the strip renders
export function useAreaStrip() {
  const { pathname } = useLocation();
  const { user, can } = useAuth();

  const workspace = workspaceForPath(pathname, user?.experience);
  if (!workspace || workspace.placement === 'footer') return null;

  const tabs = workspaceTabs(workspace, can);
  const actions = workspaceActions(workspace, can);
  if (tabs.length <= 1 && actions.length === 0) return null;

  return { workspace, tabs, actions, current: activeNavItem(pathname, user?.experience) };
}

/**
 * The area's tab strip (2026-09-25 seven-destination nav). Michael: "Use tabs
 * inside each area. Equipment, for example, opens to tabs for Overview ·
 * Fleet · Inbox. Apply that pattern to Purchasing and People & Safety."
 *
 * Rendered ONCE by each shell and computed from the same configuration as
 * the sidebar: the workspace that owns the current path, its pages as tabs
 * (tabLabel where one is set), its `action` items as buttons on the right,
 * the active page marked with aria-current. Every page keeps its own URL and
 * content — this is a strip over them, not a rewrite of them — so deep links,
 * bookmarks and the ⌘K palette all still land where they always did.
 *
 * Two shapes (compact chrome, 2026-10-10; Michael: "it uses too much space"):
 * - `bar` IS the desktop's top row, beside the bell. The tabs fill the row's
 *   height with the underline on its bottom border, and only the tab list
 *   scrolls sideways when it runs out of room — never the row, so nothing
 *   clips the bell's popover. Each tab carries a star: pin it and it appears
 *   in the sidebar's Pinned block (useNavPins — saved to the account). The
 *   star sits in the tab's own right padding, so it costs no width.
 * - `strip` is the phone's, at the top of the page, scrolling away with it.
 *   No stars: pins only show in the desktop sidebar.
 */
export function AreaTabs({ variant = 'strip' }: { variant?: 'strip' | 'bar' }) {
  const strip = useAreaStrip();
  const badges = useNavBadges();
  const { isPinned, toggle, full, saving, error: pinError } = useNavPins();
  // The bar's tab list, where it runs out of room (Reports at 1024 with the
  // sidebar open), opened with the page's own tab cut off at its end: "All
  // repo". So the open tab comes into view before the row paints, and again
  // whenever something moves it — the light beside the list grows to
  // "Offline" or "3 pending", the sidebar folds, a count lands on a tab
  // before it. Sideways only (revealTab); the page never moves.
  const [list, setList] = useState<HTMLUListElement | null>(null);
  const currentTo = strip?.current?.to;
  useLayoutEffect(() => {
    if (!list) return;
    const reveal = () => {
      const open = list.querySelector('[aria-current="page"]')?.closest('li');
      if (open) revealTab(list, open);
    };
    reveal();
    // A nicety: an observer that cannot start (a test's stub without
    // observe()) must never be what breaks the row.
    let observer: ResizeObserver | null = null;
    try {
      observer = new ResizeObserver(reveal);
      observer.observe(list);
      list.querySelectorAll('li').forEach((li) => observer?.observe(li));
    } catch {
      observer = null;
    }
    return () => observer?.disconnect();
  }, [list, currentTo]);
  if (!strip) return null;

  const { workspace, tabs, actions, current } = strip;
  const bar = variant === 'bar';

  return (
    <nav
      aria-label={`${workspace.label} sections`}
      data-testid="area-tabs"
      className={bar
        ? 'flex min-w-0 flex-1 items-stretch'
        : 'mb-0 flex items-center gap-1 overflow-x-auto border-b border-graphite-800'}
    >
      <ul
        ref={bar ? setList : undefined}
        role="list"
        className={bar
          ? 'flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          : 'flex min-w-0 flex-1 items-end gap-1'}
      >
        {tabs.map((tab) => {
          const active = current?.to === tab.to;
          const count = tab.badge ? badges[tab.badge] ?? 0 : 0;
          const pinned = isPinned(tab.to);
          return (
            <li
              key={tab.to}
              className={cn('group relative flex shrink-0', bar ? 'items-stretch' : 'items-center')}
              // A tab (or its star) reached with the keyboard comes into view:
              // the browser only scrolls to one that is wholly hidden.
              onFocus={bar ? (e) => { if (list) revealTab(list, e.currentTarget); } : undefined}
            >
              <Link
                to={tab.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2 border-b-2 text-sm transition-colors',
                  // The bar's underline sits on the row's own border.
                  bar ? 'h-full pl-3 pr-5' : '-mb-px h-10 px-3',
                  active
                    ? 'border-safety-500 font-medium text-graphite-50'
                    : 'border-transparent hover:border-graphite-600 hover:text-graphite-100',
                  // The row is graphite-900, a step lighter than the page the
                  // strip sits on: there the page's muted 400 reads at 3.3:1,
                  // under the 4.5 that 14px text needs. 300 is the sidebar's.
                  !active && (bar ? 'text-graphite-300' : 'text-graphite-400'),
                )}
              >
                {tab.tabLabel ?? tab.label}
                {count > 0 && tab.badge && (
                  <span
                    aria-label={NAV_BADGE_LABELS[tab.badge]?.(count) ?? `${count} waiting`}
                    className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-safety-500 px-1.5 text-[11px] font-semibold leading-5 text-white"
                  >
                    {count}
                  </span>
                )}
              </Link>
              {/* The pin. Shown on hover or focus, and always once pinned, so
                  the row stays quiet until somebody reaches for it. graphite-400
                  on the row's grey is 3.30:1; 500 was 2.23 (review 4). */}
              {bar && (
                <button
                  type="button"
                  onClick={() => toggle(tab.to)}
                  disabled={saving || (!pinned && full)}
                  aria-pressed={pinned}
                  aria-label={pinned ? `Unpin ${tab.label} from the sidebar` : `Pin ${tab.label} to the sidebar`}
                  title={pinned ? 'Unpin from the sidebar' : full ? 'Sidebar pins are full (8)' : 'Pin to the sidebar'}
                  className={cn(
                    'absolute right-0.5 top-1.5 flex h-4 w-4 items-center justify-center rounded text-graphite-400 transition-opacity hover:text-safety-400 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-safety-500 disabled:cursor-not-allowed',
                    pinned ? 'opacity-100 text-safety-500' : 'opacity-0 group-hover:opacity-100',
                  )}
                >
                  <Star className={cn('h-3 w-3', pinned && 'fill-current')} aria-hidden />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {/* Under the row's left end, over the page: the row has no room for a
          sentence, and it must not grow. */}
      {bar && pinError && (
        <p
          role="alert"
          className="absolute left-3 top-full z-50 mt-1 rounded-md border border-danger-500/40 bg-graphite-900 px-2 py-1 text-xs text-danger-300 shadow-lg"
        >
          Pin not saved — {pinError}
        </p>
      )}
      {actions.length > 0 && (
        <div className={cn('flex shrink-0 items-center gap-2', !bar && 'pb-1.5 pl-3')}>
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.to}
                to={action.to}
                className="flex h-8 items-center gap-1.5 rounded-md border border-graphite-700 bg-graphite-900 px-3 text-xs font-medium text-graphite-200 transition-colors hover:border-safety-500 hover:text-safety-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-safety-500"
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {action.label}
              </Link>
            );
          })}
        </div>
      )}
    </nav>
  );
}
